import React, { useEffect, useState } from 'react';
import { devPreviewUrl, getPreviewToken, sitePreviewUrl } from './previewLinks';

/* The preview iframe needs a dev renderer. Without an explicit host it points
   at localhost, which only works on the machine running that renderer -- so
   callers hide the Preview control unless a host is configured that is not
   localhost (REACT_APP_DEV_PREVIEW_HOST always counts as configured). */
export const PREVIEW_HOST = process.env.REACT_APP_DEV_PREVIEW_HOST
  || process.env.REACT_APP_DEV_HOST
  || 'http://127.0.0.1:8888';

export const previewEnabled = () => {
  if (process.env.REACT_APP_DEV_PREVIEW_HOST) return true;
  const host = process.env.REACT_APP_DEV_HOST || '';
  return !!host && !/localhost|127\.0\.0\.1/.test(host);
};

const PagePreviewModal = ({ domainId, slug, onClose }) => {
  const [viewport, setViewport] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'
  /* The renderer shows drafts and pending revisions only with a preview
     token, so the iframe waits for one. If minting fails the published page
     is shown and the bar says so. */
  const [preview, setPreview] = useState({ state: 'loading', token: null, host: null, error: null });

  useEffect(() => {
    let cancelled = false;
    getPreviewToken(domainId)
      .then(({ token, host }) => { if (!cancelled) setPreview({ state: 'ready', token, host, error: null }); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setPreview({ state: 'error', token: null, host: null, error: typeof detail === 'string' ? detail : (err?.message || 'Could not mint a preview token') });
      });
    return () => { cancelled = true; };
  }, [domainId]);

  /* A nested page is stored with its path encoded ("error%2Fps4-..."); the
     live site serves it at the decoded path, so only the preview keeps the id
     spelling. The live link used to point at the encoded one. */
  const livePath = (slug || '').replace(/%2F/gi, '/');
  const previewUrl = devPreviewUrl(PREVIEW_HOST, domainId, slug, preview.token);
  const liveUrl = `https://${preview.host || domainId.replace(/_/g, '.')}/${livePath}${livePath ? '/' : ''}`;
  const siteUrl = preview.token && preview.host ? sitePreviewUrl(preview.host, slug, preview.token) : null;

  const viewportWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#1a1a2e',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: 'white',
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Viewport toggles */}
        {['desktop', 'tablet', 'mobile'].map((vp) => (
          <button
            key={vp}
            onClick={() => setViewport(vp)}
            style={{
              padding: '5px 12px',
              backgroundColor: viewport === vp ? '#007bff' : 'transparent',
              color: 'white',
              border: viewport === vp ? '1px solid #007bff' : '1px solid #555',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            {vp === 'desktop' ? '🖥️' : vp === 'tablet' ? '📱' : '📲'}{' '}
            {vp.charAt(0).toUpperCase() + vp.slice(1)}
          </button>
        ))}

        {/* The full preview URL, selectable: it is the thing people want to
            copy into a browser or hand to someone else. */}
        <input
          readOnly
          value={previewUrl}
          onFocus={(e) => e.target.select()}
          title={previewUrl}
          style={{
            flex: 1,
            minWidth: 0,
            margin: '0 8px',
            padding: '5px 8px',
            fontSize: '12px',
            color: '#ddd',
            backgroundColor: '#222',
            border: '1px solid #555',
            borderRadius: '4px',
          }}
        />

        {/* Open in new tab */}
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '5px 12px',
            backgroundColor: '#17a2b8',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontSize: '13px',
          }}
        >
          Preview ↗
        </a>
        {siteUrl && (
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="This page in preview on the site's own domain"
            style={{
              padding: '5px 12px',
              backgroundColor: '#6f42c1',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '13px',
            }}
          >
            On site ↗
          </a>
        )}
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '5px 12px',
            backgroundColor: '#28a745',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontSize: '13px',
          }}
        >
          Live ↗
        </a>
        <button
          onClick={onClose}
          style={{
            padding: '5px 12px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>

      {preview.state === 'error' && (
        <div
          role="alert"
          style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '6px 20px', fontSize: 13, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          No preview token ({preview.error}). Showing the published page: drafts and pending revisions will not appear.
        </div>
      )}

      {/* iframe container */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#2d2d2d',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {preview.state === 'loading' ? (
          <div style={{ color: '#ddd', alignSelf: 'center' }}>Preparing preview…</div>
        ) : (
        <iframe
          src={previewUrl}
          title={`Preview: ${slug}`}
          style={{
            width: viewportWidths[viewport],
            maxWidth: '100%',
            height: '100%',
            border: viewport !== 'desktop' ? '1px solid #555' : 'none',
            borderRadius: viewport !== 'desktop' ? '8px' : '0',
            backgroundColor: 'white',
          }}
        />
        )}
      </div>
    </div>
  );
};

export default PagePreviewModal;
